/**
 * I'm not sure how to, or if it's even worth it to, write unit tests for this util
 */

import { createContext, useContext } from 'react'
import { NODE_ENV } from 'config'

interface CreateContext<T> {
  /**
   * the provider you should use in an exported Provider component
   */
  Provider: React.Provider<T>
  /**
   * a function to return the context of the provider
   *
   * in strict contexts, this will throw and error when no provider is found
   * above the invocation in the component tree
   */
  useContextFn: () => T
}

/**
 * initializes a strict context provider and function to use the context
 *
 * @param displayName the name of the context for dev purposes.
 * @see {@link CreateContext}
 * @example
 * const {
 *  Provider,
 *  useContextFn
 * } = createStrictContext<{ ... }>('FooContext')
 *
 * export const useFooContext = useContextFn
 *
 * const FooProvider: React.FC = ({children}) => {
 *  // get information for context
 *  return <Provider value={{...}}>{children}</Provider>
 * }
 *
 * export default FooProvider
 */
export function createStrictContext<T>(displayName: string): CreateContext<T> {
  /**
   * it seems counter intuitive to initialize the context with undefined as
   * the default value, but it allows the {@link useContextFn} to throw if the
   * provider isn't used
   */
  const Context = initializeContext<T>(displayName, undefined)

  function useContextFn() {
    /**
     * using this here on purpose because it's the same pattern we'd use for a
     * generic context
     */
    return useGenericStrictContext(Context, displayName)
  }

  return { Provider: Context.Provider, useContextFn }
}

const warnings = new Map<string, boolean>()

/**
 * initializes an optional context provider and function to use the context
 *
 * you must define default values to use in the event
 *
 * @param options {@link CreateOptionsContextOptions}
 * @see {@link CreateContext}
 * @example
 * const {
 *  Provider,
 *  useContextFn
 * } = createOptionalContext<{ ... }>({
 *  displayName: 'BarContext',
 *  defaults: {
 *   str: 'string',
 *   num: -1,
 *   data: [...],
 *  },
 *  usingDefaults: ({ num }) => num === -1
 * })
 *
 * export const useBarContext = useContextFn
 *
 * const BarProvider: React.FC = ({children}) => {
 *  // get information for context
 *  return (
 *   <Provider
 *     value={{
 *       str: 'the correct string after initialization',
 *       num: 42,
 *       data: [ ... ],
 *     }}
 *   >
 *    {children}
 *   </Provider>
 *  )
 * }
 *
 * export default BarProvider
 */
export function createOptionalContext<T>({
  defaults,
  displayName,
  usingDefaults,
}: CreateOptionsContextOptions<T>): CreateContext<T> {
  const Context = initializeContext(displayName, defaults)

  function useContextFn() {
    const context = useContext(Context)
    if (
      !warnings.has(displayName) &&
      usingDefaults(context) &&
      NODE_ENV !== 'production'
    ) {
      warnings.set(displayName, true)
      console.warn(
        `${message(displayName)}. The following defaults will be used`,
        defaults
      )
    }
    return context
  }

  return { Provider: Context.Provider, useContextFn }
}

interface CreateOptionsContextOptions<T> {
  /**
   * the name of the context for dev purposes.
   */
  displayName: string
  /**
   * an object containing the default values of the context
   *
   * these will be used if you don't add the provider high enough in the
   * component tree for the {@link CreateContext.useContextFn|useContextFn} to
   * return values you expect
   */
  defaults: T
  /**
   * a callback to determine whether defaults are being used or not
   *
   * this will throw up a warning in the console if true
   *
   * @param context the current context
   */
  usingDefaults: (context: T) => boolean
}

function message(displayName: string) {
  return `${displayName} cannot be found in the component tree above the invocation of this function` as const
}

function initializeContext<T>(displayName: string, defaults: T | undefined) {
  if (!displayName.toLowerCase().includes('context'))
    console.warn(
      `Non standard context name detected: ${displayName}. You should use Context`
    )
  const Context = createContext(defaults as T)
  Context.displayName = displayName
  return Context
}

/**
 * wraps the {@link useContext} hook and validates the existence of an
 * associated provider
 *
 * this wrapper should only be used when creating a context that needs generics
 * and requires you to take extra care as to not leak the Context
 * 
 * you should use {@link createStrictContext} or {@link createOptionalContext}
 * for non-generic contexts

 * @example
 * interface GenericContext<T> {
 *  prop1: T
 *  prop2: string
 * }
 *
 * // use null on purpose because we can't use a generic type with createStrictContext
 * const GenericContext = createContext(null)
 *
 * export function useGenericContext<T>() {
 *  return useStrictContext<GenericContext<T>>(GenericContext, 'GenericContext')
 * }
 *
 * interface GenericContextProviderProps<T> { ... }
 *
 * function GenericContextProvider<T>({
 *  children,
 *  ...
 * }: React.PropsWithChildren<GenericContextProviderProps<T>>) {
 *  const value: GenericContext<T> = { ... }
 *  return (
 *    // this is being done on purpose because we can't use createStrictContext with generics
 *    <GenericContext.Provider value={value as unknown as null}>
 *      {children}
 *    </GenericContext.Provider>
 *  )
 * }
 *
 * @param context a previously created context using {@link React.createContext|createContext}
 * @param displayName the name of the provider component that should exist above the context hook
 * @throws an error if the {@link UseSafeContextOptions.providerName|provider} isn't found above a {@link UseSafeContextOptions.contextHookName|context}
 * @see {@link createStrictContext}
 */
export function useGenericStrictContext<T = unknown>(
  context: React.Context<T> | React.Context<null>,
  displayName: string
) {
  /**
   * we're casting this as `any` because you have to initialize a null context with
   * generic providers
   */
  const existingContext = useContext<T>(context as React.Context<T>)
  if (existingContext === null || existingContext === undefined)
    throw new Error(message(displayName))
  return existingContext
}
